import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { PoComboFilter, PoComboOption } from '@po-ui/ng-components';

import { GitService } from '../../core/api/git.service';
import { GitRepository } from '../../core/interfaces/git.interface';
import { getApiErrorMessage } from '../../core/utils/http-error-message.util';

/**
 * Busca de repositório no provedor.
 *
 * O projeto é etapa ANTES desta busca nos provedores que o têm, e não porque a API o
 * exija — ela não exige —, mas pelo que custa dispensá-lo. No Azure DevOps, buscar sem
 * projeto faz o adaptador listar os projetos, resolver o descritor de grupo de CADA UM e
 * então listar os repositórios de cada projeto acessível: com N projetos na organização e
 * acesso a M, são ~N + M + 3 chamadas, sem cache nenhum. Com o projeto escolhido, são 3 a
 * 5. Numa organização de 40 projetos a diferença é ~58 contra 4, a cada busca.
 *
 * Onde não há projeto — Gitea — a busca custa de 1 a 3 chamadas, constante, e o próprio
 * campo já resolve `dono/` listando tudo daquele dono. Ali a etapa não teria o que pagar.
 *
 * É combo, e não po-lookup: o lookup abre a busca num po-modal PRÓPRIO, e este campo vive
 * dentro do po-modal do cadastro. Dois modais empilhados disputam o mesmo overlay e a tela
 * trava.
 */
@Injectable()
export class AdminGitRepositorySearchService implements PoComboFilter {
  provider = '';
  /** Projeto escolhido na etapa anterior, nos provedores que a têm. Vazio nos demais. */
  project = '';
  /**
   * Mínimo de caracteres antes de consultar.
   *
   * Zero onde a busca é barata; dois onde ela varre a organização inteira caso o projeto
   * não tenha sido escolhido — e ali o campo só é liberado depois do projeto, de modo que
   * o mínimo protege apenas contra a digitação em si.
   */
  minLength = 0;
  notify: ((error: string) => void) | null = null;

  private readonly byFullName = new Map<string, GitRepository>();

  constructor(private gitService: GitService) {}

  getFilteredData(params: { property?: string; value?: string }): Observable<PoComboOption[]> {
    const provider = this.provider.trim();
    if (!provider) {
      return of([]);
    }
    const query = String(params.value ?? '').trim();
    if (query.length < this.minLength) {
      this.byFullName.clear();
      this.notify?.('');
      return of([]);
    }

    return this.gitService.listRepositories(provider, query, LIST_LIMIT, this.project).pipe(
      tap(repos => {
        repos.forEach(repo => this.byFullName.set(repo.fullName, repo));
        this.notify?.('');
      }),
      map(repos => repos.map(repo => ({ label: labelFor(repo), value: repo.fullName }))),
      catchError(error => {
        this.notify?.(getApiErrorMessage(error, 'Não foi possível listar os repositórios do provedor.'));
        return of([]);
      })
    );
  }

  /**
   * Resolve um valor já escolhido de volta para a linha.
   *
   * O combo chama isto ao receber um valor de fora — no nosso caso, quando o campo é
   * preenchido em código. Sem cache, consulta o provedor pelo próprio nome.
   */
  getObjectByValue(value: string | number): Observable<PoComboOption> {
    const fullName = String(value ?? '');
    const cached = this.byFullName.get(fullName);
    if (cached) {
      return of({ label: labelFor(cached), value: cached.fullName });
    }
    if (!this.provider.trim() || fullName.trim() === '') {
      return of({ label: fullName, value });
    }

    return this.gitService.listRepositories(this.provider, fullName, LIST_LIMIT, this.project).pipe(
      tap(repos => repos.forEach(repo => this.byFullName.set(repo.fullName, repo))),
      map(repos => {
        const found = repos.find(repo => repo.fullName === fullName);
        return found ? { label: labelFor(found), value: found.fullName } : { label: fullName, value };
      }),
      catchError(() => of({ label: fullName, value }))
    );
  }

  repositoryFor(value: string): GitRepository | null {
    return this.byFullName.get(value) ?? null;
  }

  reset(): void {
    this.provider = '';
    this.project = '';
    this.minLength = 0;
    this.byFullName.clear();
    this.notify?.('');
  }

  /** Esquece o que foi listado, sem soltar provedor e projeto. */
  clearRepositories(): void {
    this.byFullName.clear();
    this.notify?.('');
  }
}

const LIST_LIMIT = 50;

/**
 * Rótulo da opção: caminho, branch padrão e a situação.
 *
 * "Já cadastrado" é a informação que evita o 409 depois do formulário inteiro preenchido.
 */
function labelFor(repo: GitRepository): string {
  const parts = [repo.fullName];
  if (repo.defaultBranch) { parts.push(repo.defaultBranch); }
  if (repo.registered) { parts.push('já cadastrado'); }
  return parts.join(' · ');
}
